import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { trackBackendHit } from "@/utils/trafficTracker";
const sanitizeApiBaseUrl = raw => String(raw ?? "").trim().replace(/\/+$/, "");
const resolveApiBaseUrl = () => {
  const fromEnv = process.env.NEXT_PUBLIC_API_BASE_URL ?? process.env.API_BASE_URL ?? process.env.BACKEND_URL;
  const sanitized = sanitizeApiBaseUrl(fromEnv);

  // Default to local backend when not configured (avoids 404s on Next routes)
  if (!sanitized && process.env.NODE_ENV !== "production") {
    return "http://localhost:3001";
  }

  // Another common footgun: env is set to the Next.js dev server origin.
  // In this project the backend typically runs on :3001, so pointing to :3000
  // makes API requests hit Next page routes (HTML) instead of the backend.
  if (process.env.NODE_ENV !== "production" && /^https?:\/\/localhost:3000$/i.test(sanitized)) {
    return "http://localhost:3001";
  }

  // Allow explicit relative proxy base (e.g. "/api") to work with Next.js rewrites.
  if (sanitized.startsWith("/")) {
    return sanitized;
  }

  // Avoid a common footgun: empty base URL makes requests hit the Next.js origin
  // (e.g. client requesting /organizations will call the /organizations page route).
  if (!sanitized) {
    return process.env.NODE_ENV === "production" ? "" : "/api";
  }

  // In the browser, Docker Compose service names like "http://backend:3001" do not
  // resolve (ERR_NAME_NOT_RESOLVED). In local dev, prefer a working localhost fallback.
  if (process.env.NODE_ENV !== "production") {
    try {
      const parsed = new URL(sanitized);
      const hostname = parsed.hostname.toLowerCase();
      const isIp = /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);
      const isLocalhostLike = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "0.0.0.0" || hostname === "host.docker.internal";
      const allowRemoteApiInDev = String(process.env.NEXT_PUBLIC_ALLOW_REMOTE_API_IN_DEV ?? "").trim().toLowerCase() === "true" || String(process.env.NEXT_PUBLIC_ALLOW_REMOTE_API_IN_DEV ?? "").trim() === "1";

      // Another common footgun: .env.local copied from a production example points
      // to https://api.productcertification.com, which will fail locally (CORS/DNS)
      // and isn't what you want when running the backend on localhost.
      if (!allowRemoteApiInDev && hostname.endsWith("productcertification.com")) {
        // eslint-disable-next-line no-console
        console.warn(`[api] NEXT_PUBLIC_API_BASE_URL is set to ${sanitized} while running in dev; falling back to http://localhost:3001. Set NEXT_PUBLIC_ALLOW_REMOTE_API_IN_DEV=1 to override.`);
        return "http://localhost:3001";
      }

      // Heuristic: hostnames without dots (e.g. "backend") typically won't resolve
      // in the browser. Keep fully-qualified names (with dots) as-is.
      if (!isLocalhostLike && !isIp && !hostname.includes(".")) {
        // eslint-disable-next-line no-console
        console.warn(`[api] NEXT_PUBLIC_API_BASE_URL host "${hostname}" may not resolve in the browser; falling back to http://localhost:3001 for local dev.`);
        return "http://localhost:3001";
      }
    } catch {
      return "http://localhost:3001";
    }
  }
  return sanitized;
};
export const API_BASE_URL = resolveApiBaseUrl();

// Base types inferred from Postman collection. Adjust as your backend returns.

/** Professionals — individual practitioner profile pages (rose accent). */

/** Row shape served by GET /promotions?placement=… */

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  // Hung-request guard: a fetch whose response body stalls (seen live:
  // Chrome incognito failing a disk-cache write mid-body leaves
  // response.json() pending FOREVER) keeps RTK isLoading=true eternally,
  // stranding every skeleton gated on it. Abort settles the query as
  // rejected, so loading flags clear and empty-state paths render.
  timeout: 20_000,
  prepareHeaders: headers => {
    const token = (() => {
      if (typeof window === "undefined") return null;
      try {
        return window.localStorage?.getItem("pc_auth_token") || null;
      } catch {
        return null;
      }
    })();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return headers;
  }
});

// Wrap the base query so the traffic tracker can count every backend hit that
// actually leaves the browser (RTK dedupes/caches in front of this, so a hit
// here means a real network request). No-op cost when tracking is off.
const instrumentedBaseQuery = async (args, apiCtx, extraOptions) => {
  const start = typeof performance !== "undefined" ? performance.now() : Date.now();
  const result = await rawBaseQuery(args, apiCtx, extraOptions);
  try {
    const endpoint = typeof args === "string" ? args : args.url;
    const method = typeof args === "string" ? "GET" : args.method ?? "GET";
    const end = typeof performance !== "undefined" ? performance.now() : Date.now();
    trackBackendHit({
      endpoint,
      method,
      status: result.meta?.response?.status,
      durationMs: Math.round(end - start),
      source: "rtk"
    });
  } catch {
    // Instrumentation must never break a query.
  }
  return result;
};
export const api = createApi({
  reducerPath: "api",
  keepUnusedDataFor: 600,
  baseQuery: instrumentedBaseQuery,
  tagTypes: ["Users", "Organizations", "OrgSubmissions", "Tasks", "Events", "EventSubmissions", "Regulations", "RegulationSubmissions", "Articles", "ArticleSubmissions", "Notifications", "OrganizationInquiries", "NewsletterSignups", "DeactivationMode", "Promotions", "PromotionLogs", "PromotionMode", "SiteSettings", "Analytics", "SuggestedEdits", "OrgDrafts", "Professionals", "ProfessionalSubmissions", "OrgMembers", "OrgBids", "OrgContentCounts", "PromotionInvoices"],
  endpoints: builder => ({
    // Users
    createUser: builder.mutation({
      query: body => ({
        url: "/users",
        method: "POST",
        body
      }),
      invalidatesTags: ["Users"]
    }),
    getUsers: builder.query({
      query: () => ({
        url: "/users",
        method: "GET"
      }),
      providesTags: ["Users"]
    }),
    getUserById: builder.query({
      query: id => ({
        url: `/users/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{
        type: "Users",
        id
      }]
    }),
    updateUser: builder.mutation({
      query: ({
        id,
        patch
      }) => ({
        url: `/users/${id}`,
        method: "PATCH",
        body: patch
      }),
      invalidatesTags: (result, error, {
        id
      }) => [{
        type: "Users",
        id
      }, "Users"]
    }),
    deleteUser: builder.mutation({
      query: id => ({
        url: `/users/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Users",
        id
      }, "Users"]
    }),
    // Deactivation Mode (admin only)
    getDeactivationMode: builder.query({
      query: () => ({
        url: "/auth/deactivation-mode",
        method: "GET"
      }),
      providesTags: ["DeactivationMode"]
    }),
    setDeactivationMode: builder.mutation({
      query: enabled => ({
        url: "/auth/deactivation-mode",
        method: "PATCH",
        body: {
          enabled
        }
      }),
      invalidatesTags: ["DeactivationMode"]
    }),
    // Promotion Mode (admin only)
    getPromotionMode: builder.query({
      query: () => ({
        url: "/auth/promotion-mode",
        method: "GET"
      }),
      providesTags: ["PromotionMode"]
    }),
    setPromotionMode: builder.mutation({
      query: enabled => ({
        url: "/auth/promotion-mode",
        method: "PATCH",
        body: {
          enabled
        }
      }),
      invalidatesTags: ["PromotionMode"]
    }),
    // Site Settings (GET public, PATCH admin only)
    getSiteSettings: builder.query({
      query: () => ({
        url: "/site-settings",
        method: "GET"
      }),
      providesTags: ["SiteSettings"],
      // Keep for only 60 s so changes made by an admin propagate quickly
      // to all connected clients without them needing to hard-refresh.
      keepUnusedDataFor: 60
    }),
    updateSiteSettings: builder.mutation({
      query: dto => ({
        url: "/site-settings",
        method: "PATCH",
        body: dto
      }),
      invalidatesTags: ["SiteSettings"]
    }),
    // ── Global traffic analytics (admin) ──
    getAnalyticsSummary: builder.query({
      query: arg => {
        const a = typeof arg === "number" ? {
          days: arg
        } : arg && typeof arg === "object" ? arg : {
          days: 30
        };
        const qs = new URLSearchParams();
        // from/to OVERRIDE the rolling window — never send both.
        if (a.from || a.to) {
          if (a.from) qs.set("from", a.from);
          if (a.to) qs.set("to", a.to);
        } else {
          qs.set("days", String(a.days ?? 30));
        }
        return {
          url: `/analytics/summary?${qs.toString()}`,
          method: "GET"
        };
      },
      providesTags: ["Analytics"],
      keepUnusedDataFor: 60
    }),
    getAdminTeam: builder.query({
      query: () => ({
        url: "/admin/team",
        method: "GET"
      }),
      keepUnusedDataFor: 300
    }),
    getAdminPromotionBids: builder.query({
      query: () => ({
        url: "/admin/promotion-bids",
        method: "GET"
      }),
      providesTags: ["OrgBids"]
    }),
    declinePromotionBid: builder.mutation({
      query: ({
        bidId,
        reason
      }) => ({
        url: `/admin/promotion-bids/${bidId}/decline`,
        method: "POST",
        body: {
          reason
        }
      }),
      invalidatesTags: ["OrgBids"]
    }),
    /** Approve → invoice raised + emailed to the requester; a $0 free-
     *  intro total activates the promotion immediately. */
    approvePromotionBid: builder.mutation({
      query: ({
        bidId
      }) => ({
        url: `/admin/promotion-bids/${bidId}/approve`,
        method: "POST"
      }),
      invalidatesTags: ["OrgBids", "PromotionInvoices", "Promotions"],
      async onQueryStarted() {
        // Free-intro approvals create a live promotion — same
        // placement-cache clear as the direct promotion mutations.
        (await import("@/utils/localStore")).clearPromotionsCache();
      }
    }),
    getAdminPromotionInvoices: builder.query({
      query: () => ({
        url: "/admin/promotion-invoices",
        method: "GET"
      }),
      providesTags: ["PromotionInvoices"]
    }),
    /** The manual payment-recording seam — activates the promotion. */
    markPromotionInvoicePaid: builder.mutation({
      query: ({
        invoiceId,
        note
      }) => ({
        url: `/admin/promotion-invoices/${invoiceId}/mark-paid`,
        method: "POST",
        body: note ? {
          note
        } : {}
      }),
      invalidatesTags: ["OrgBids", "PromotionInvoices", "Promotions"],
      async onQueryStarted() {
        (await import("@/utils/localStore")).clearPromotionsCache();
      }
    }),
    /** Public gate check — the hash never leaves the backend. */
    verifySitePassword: builder.mutation({
      query: password => ({
        url: "/site-settings/verify-password",
        method: "POST",
        body: {
          password
        }
      })
    }),
    getAnalyticsNotableDays: builder.query({
      query: () => ({
        url: "/analytics/notable-days",
        method: "GET"
      }),
      providesTags: ["Analytics"],
      keepUnusedDataFor: 60
    }),
    runAnalyticsRollup: builder.mutation({
      query: () => ({
        url: "/analytics/rollup",
        method: "POST"
      }),
      invalidatesTags: ["Analytics"]
    }),
    // Suggested Edits (guest submissions; admin list/delete)
    submitSuggestedEdit: builder.mutation({
      query: dto => ({
        url: "/suggested-edits",
        method: "POST",
        body: dto
      }),
      invalidatesTags: ["SuggestedEdits"]
    }),
    getSuggestedEdits: builder.query({
      query: () => ({
        url: "/suggested-edits",
        method: "GET"
      }),
      providesTags: ["SuggestedEdits"]
    }),
    deleteSuggestedEdit: builder.mutation({
      query: id => ({
        url: `/suggested-edits/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["SuggestedEdits"]
    }),
    // Organizations
    createOrganization: builder.mutation({
      query: body => ({
        url: "/organizations",
        method: "POST",
        body
      }),
      invalidatesTags: ["Organizations"]
    }),
    getOrganizations: builder.query({
      query: () => ({
        url: "/organizations",
        method: "GET"
      }),
      providesTags: ["Organizations"]
    }),
    getOrganizationById: builder.query({
      query: id => ({
        url: `/organizations/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{
        type: "Organizations",
        id
      }]
    }),
    updateOrganization: builder.mutation({
      query: ({
        id,
        patch
      }) => ({
        url: `/organizations/${id}`,
        method: "PATCH",
        body: patch
      }),
      invalidatesTags: (result, error, {
        id
      }) => [{
        type: "Organizations",
        id
      }, "Organizations"]
    }),
    deleteOrganization: builder.mutation({
      query: id => ({
        url: `/organizations/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Organizations",
        id
      }, "Organizations"]
    }),
    getMyOrganizations: builder.query({
      query: () => ({
        url: "/organizations/my",
        method: "GET"
      }),
      providesTags: ["Organizations"]
    }),
    searchOrganizations: builder.query({
      query: q => ({
        url: "/organizations/search",
        method: "GET",
        params: {
          q
        }
      }),
      providesTags: ["Organizations"]
    }),
    // Events
    createEvent: builder.mutation({
      query: body => ({
        url: "/events",
        method: "POST",
        body
      }),
      invalidatesTags: ["Events"]
    }),
    getEvents: builder.query({
      query: () => ({
        url: "/events",
        method: "GET"
      }),
      providesTags: ["Events"]
    }),
    getEventsByOrganization: builder.query({
      query: organizationId => ({
        url: `/events?organizationId=${organizationId}`,
        method: "GET"
      }),
      providesTags: ["Events"],
      keepUnusedDataFor: 300
    }),
    getEventById: builder.query({
      query: id => ({
        url: `/events/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{
        type: "Events",
        id
      }]
    }),
    updateEvent: builder.mutation({
      query: ({
        id,
        patch
      }) => ({
        url: `/events/${id}`,
        method: "PATCH",
        body: patch
      }),
      invalidatesTags: (result, error, {
        id
      }) => [{
        type: "Events",
        id
      }, "Events"]
    }),
    deleteEvent: builder.mutation({
      query: id => ({
        url: `/events/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Events",
        id
      }, "Events"]
    }),
    deactivateEvent: builder.mutation({
      query: id => ({
        url: `/events/${id}/deactivate`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Events",
        id
      }, "Events"]
    }),
    reactivateEvent: builder.mutation({
      query: id => ({
        url: `/events/${id}/reactivate`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Events",
        id
      }, "Events"]
    }),
    approveEvent: builder.mutation({
      query: id => ({
        url: `/events/${id}/approve`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Events",
        id
      }, "Events"]
    }),
    getPendingEvents: builder.query({
      query: () => ({
        url: "/events/pending",
        method: "GET"
      }),
      providesTags: ["Events"]
    }),
    getDeactivatedEvents: builder.query({
      query: () => ({
        url: "/events/deactivated",
        method: "GET"
      }),
      providesTags: ["Events"]
    }),
    // Event Submissions
    submitEvent: builder.mutation({
      query: body => ({
        url: "/event-submissions",
        method: "POST",
        body
      }),
      invalidatesTags: ["EventSubmissions"],
      async onQueryStarted(_, {
        queryFulfilled
      }) {
        try {
          await queryFulfilled;
          // The user now has submissions — drop the "none" memo so
          // the events index resumes fetching /mine.
          (await import("@/utils/localStore")).clearUserPresenceMemo("event-submissions");
        } catch {
          // submission failed — memo unchanged
        }
      }
    }),
    submitGuestEvent: builder.mutation({
      query: body => ({
        url: "/public/event-submissions/guest",
        method: "POST",
        body
      }),
      invalidatesTags: ["EventSubmissions"]
    }),
    deleteGuestEventSubmission: builder.mutation({
      query: id => ({
        url: `/admin/event-submissions/guest/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["EventSubmissions"]
    }),
    getMyEventSubmissions: builder.query({
      query: () => ({
        url: "/event-submissions/mine",
        method: "GET"
      }),
      providesTags: ["EventSubmissions"]
    }),
    getEventSubmissionTasks: builder.query({
      query: id => ({
        url: `/event-submissions/${id}/tasks`,
        method: "GET"
      }),
      providesTags: ["Tasks"]
    }),
    replyToEventTask: builder.mutation({
      query: ({
        submissionId,
        editId,
        ...body
      }) => ({
        url: `/event-submissions/${submissionId}/tasks/${editId}/messages`,
        method: "POST",
        body
      }),
      invalidatesTags: ["Tasks"]
    }),
    getEventDeactivationHistory: builder.query({
      query: slug => ({
        url: `/event-submissions/deactivation-history/${encodeURIComponent(slug)}`,
        method: "GET"
      })
    }),
    // Admin Event Submissions
    getAdminEventSubmissions: builder.query({
      query: params => ({
        url: "/admin/event-submissions",
        method: "GET",
        params: params || undefined
      }),
      providesTags: ["EventSubmissions"]
    }),
    approveEventSubmission: builder.mutation({
      query: id => ({
        url: `/admin/event-submissions/${id}/approve`,
        method: "POST"
      }),
      invalidatesTags: ["EventSubmissions", "Events"]
    }),
    rejectEventSubmission: builder.mutation({
      query: ({
        id,
        dto
      }) => ({
        url: `/admin/event-submissions/${id}/reject`,
        method: "POST",
        body: dto || {}
      }),
      invalidatesTags: ["EventSubmissions", "Tasks"]
    }),
    getAdminEventDeactivationHistory: builder.query({
      query: slug => ({
        url: `/admin/event-submissions/deactivation-history/${encodeURIComponent(slug)}`,
        method: "GET"
      })
    }),
    discardEventSubmission: builder.mutation({
      query: id => ({
        url: `/event-submissions/${id}/discard`,
        method: "PATCH"
      }),
      invalidatesTags: ["EventSubmissions"],
      async onQueryStarted(_, {
        queryFulfilled
      }) {
        try {
          await queryFulfilled;
          (await import("@/utils/localStore")).clearUserPresenceMemo("event-submissions");
        } catch {
          // discard failed — memo unchanged
        }
      }
    }),
    // Regulations
    createRegulation: builder.mutation({
      query: body => ({
        url: "/regulations",
        method: "POST",
        body
      }),
      invalidatesTags: ["Regulations"]
    }),
    getRegulations: builder.query({
      query: () => ({
        url: "/regulations",
        method: "GET"
      }),
      providesTags: ["Regulations"]
    }),
    getRegulationsByOrganization: builder.query({
      query: organizationId => ({
        url: `/regulations?organizationId=${organizationId}`,
        method: "GET"
      }),
      // Scoped tag so an approval can refetch ONE org's related
      // content without touching other orgs' cached queries; the
      // bare tag keeps whole-type invalidations working too.
      providesTags: (result, error, organizationId) => ["Regulations", {
        type: "Regulations",
        id: `org-${organizationId}`
      }],
      keepUnusedDataFor: 300
    }),
    getProfessionalsByOrganization: builder.query({
      query: organizationId => ({
        url: `/professionals?organizationId=${organizationId}`,
        method: "GET"
      }),
      providesTags: (result, error, organizationId) => ["Professionals", {
        type: "Professionals",
        id: `org-${organizationId}`
      }],
      keepUnusedDataFor: 300
    }),
    getRegulationById: builder.query({
      query: id => ({
        url: `/regulations/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{
        type: "Regulations",
        id
      }]
    }),
    updateRegulation: builder.mutation({
      query: ({
        id,
        patch
      }) => ({
        url: `/regulations/${id}`,
        method: "PATCH",
        body: patch
      }),
      invalidatesTags: (result, error, {
        id
      }) => [{
        type: "Regulations",
        id
      }, "Regulations"]
    }),
    deleteRegulation: builder.mutation({
      query: id => ({
        url: `/regulations/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Regulations",
        id
      }, "Regulations"]
    }),
    deleteRegulationUpdate: builder.mutation({
      query: ({
        id,
        timestamp
      }) => ({
        url: `/regulations/${encodeURIComponent(id)}/updates/${encodeURIComponent(timestamp)}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, {
        id
      }) => [{
        type: "Regulations",
        id
      }, "Regulations"]
    }),
    deactivateRegulation: builder.mutation({
      query: id => ({
        url: `/regulations/${id}/deactivate`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Regulations",
        id
      }, "Regulations"]
    }),
    reactivateRegulation: builder.mutation({
      query: id => ({
        url: `/regulations/${id}/reactivate`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Regulations",
        id
      }, "Regulations"]
    }),
    approveRegulation: builder.mutation({
      query: id => ({
        url: `/regulations/${id}/approve`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Regulations",
        id
      }, "Regulations"]
    }),
    getPendingRegulations: builder.query({
      query: () => ({
        url: "/regulations/pending",
        method: "GET"
      }),
      providesTags: ["Regulations"]
    }),
    getDeactivatedRegulations: builder.query({
      query: () => ({
        url: "/regulations/deactivated",
        method: "GET"
      }),
      providesTags: ["Regulations"]
    }),
    // Regulation Submissions
    submitRegulation: builder.mutation({
      query: body => ({
        url: "/regulation-submissions",
        method: "POST",
        body
      }),
      invalidatesTags: ["RegulationSubmissions"]
    }),
    submitGuestRegulation: builder.mutation({
      query: body => ({
        url: "/public/regulation-submissions/guest",
        method: "POST",
        body
      }),
      invalidatesTags: ["RegulationSubmissions"]
    }),
    deleteGuestRegulationSubmission: builder.mutation({
      query: id => ({
        url: `/admin/regulation-submissions/guest/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["RegulationSubmissions"]
    }),
    getMyRegulationSubmissions: builder.query({
      query: () => ({
        url: "/regulation-submissions/mine",
        method: "GET"
      }),
      providesTags: ["RegulationSubmissions"]
    }),
    getRegulationSubmissionTasks: builder.query({
      query: id => ({
        url: `/regulation-submissions/${id}/tasks`,
        method: "GET"
      }),
      providesTags: ["Tasks"]
    }),
    replyToRegulationTask: builder.mutation({
      query: ({
        submissionId,
        editId,
        ...body
      }) => ({
        url: `/regulation-submissions/${submissionId}/tasks/${editId}/messages`,
        method: "POST",
        body
      }),
      invalidatesTags: ["Tasks"]
    }),
    getRegulationDeactivationHistory: builder.query({
      query: slug => ({
        url: `/regulation-submissions/deactivation-history/${encodeURIComponent(slug)}`,
        method: "GET"
      })
    }),
    // Admin Regulation Submissions
    getAdminRegulationSubmissions: builder.query({
      query: params => ({
        url: "/admin/regulation-submissions",
        method: "GET",
        params: params || undefined
      }),
      providesTags: ["RegulationSubmissions"]
    }),
    approveRegulationSubmission: builder.mutation({
      query: id => ({
        url: `/admin/regulation-submissions/${id}/approve`,
        method: "POST"
      }),
      invalidatesTags: ["RegulationSubmissions", "Regulations"]
    }),
    rejectRegulationSubmission: builder.mutation({
      query: ({
        id,
        dto
      }) => ({
        url: `/admin/regulation-submissions/${id}/reject`,
        method: "POST",
        body: dto || {}
      }),
      invalidatesTags: ["RegulationSubmissions", "Tasks"]
    }),
    getAdminRegulationDeactivationHistory: builder.query({
      query: slug => ({
        url: `/admin/regulation-submissions/deactivation-history/${encodeURIComponent(slug)}`,
        method: "GET"
      })
    }),
    // Professionals (rose) — individual practitioner profile pages.
    // Mirrors the regulations endpoint family.
    getProfessionals: builder.query({
      query: () => ({
        url: "/professionals",
        method: "GET"
      }),
      providesTags: ["Professionals"]
    }),
    getProfessionalById: builder.query({
      query: id => ({
        url: `/professionals/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{
        type: "Professionals",
        id
      }]
    }),
    /**
     * The signed-in user's own published professional profile. A 404
     * ("no published profile yet" — the default for every account) is a
     * SUCCESS state resolving to `null`; only real failures (network,
     * 5xx) surface as errors, so callers can distinguish "private
     * profile" from "couldn't check".
     */
    getMyProfessional: builder.query({
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const res = await baseQuery({
          url: "/professionals/my",
          method: "GET"
        });
        if (res.error) {
          if (res.error.status === 404) return {
            data: null
          };
          return {
            error: res.error
          };
        }
        return {
          data: res.data
        };
      },
      providesTags: ["Professionals"]
    }),
    createProfessional: builder.mutation({
      query: body => ({
        url: "/professionals",
        method: "POST",
        body
      }),
      invalidatesTags: ["Professionals"]
    }),
    updateProfessional: builder.mutation({
      query: ({
        id,
        patch
      }) => ({
        url: `/professionals/${id}`,
        method: "PATCH",
        body: patch
      }),
      invalidatesTags: (result, error, {
        id
      }) => [{
        type: "Professionals",
        id
      }, "Professionals"]
    }),
    deleteProfessional: builder.mutation({
      query: id => ({
        url: `/professionals/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Professionals",
        id
      }, "Professionals"]
    }),
    deactivateProfessional: builder.mutation({
      query: id => ({
        url: `/professionals/${id}/deactivate`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Professionals",
        id
      }, "Professionals"]
    }),
    reactivateProfessional: builder.mutation({
      query: id => ({
        url: `/professionals/${id}/reactivate`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Professionals",
        id
      }, "Professionals"]
    }),
    approveProfessional: builder.mutation({
      query: id => ({
        url: `/professionals/${id}/approve`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Professionals",
        id
      }, "Professionals"]
    }),
    getPendingProfessionals: builder.query({
      query: () => ({
        url: "/professionals/pending",
        method: "GET"
      }),
      providesTags: ["Professionals"]
    }),
    getDeactivatedProfessionals: builder.query({
      query: () => ({
        url: "/professionals/deactivated",
        method: "GET"
      }),
      providesTags: ["Professionals"]
    }),
    // Professional Submissions
    submitProfessional: builder.mutation({
      query: body => ({
        url: "/professional-submissions",
        method: "POST",
        body
      }),
      invalidatesTags: ["ProfessionalSubmissions"],
      async onQueryStarted(_, {
        queryFulfilled
      }) {
        try {
          await queryFulfilled;
          // The publish flow went through — drop the "no profile"
          // memo so the professionals index re-checks /my instead
          // of assuming (this is the mutation create.tsx actually
          // calls; approval later also bumps the professionals
          // version, which clears the memo on other devices).
          (await import("@/utils/localStore")).clearUserPresenceMemo("professional-profile");
        } catch {
          // submission failed — memo unchanged
        }
      }
    }),
    getMyProfessionalSubmissions: builder.query({
      query: () => ({
        url: "/professional-submissions/mine",
        method: "GET"
      }),
      providesTags: ["ProfessionalSubmissions"]
    }),
    // Admin Professional Submissions
    getAdminProfessionalSubmissions: builder.query({
      query: params => ({
        url: "/admin/professional-submissions",
        method: "GET",
        params: params || undefined
      }),
      providesTags: ["ProfessionalSubmissions"]
    }),
    approveProfessionalSubmission: builder.mutation({
      query: id => ({
        url: `/admin/professional-submissions/${id}/approve`,
        method: "POST"
      }),
      invalidatesTags: ["ProfessionalSubmissions", "Professionals"]
    }),
    rejectProfessionalSubmission: builder.mutation({
      query: ({
        id,
        dto
      }) => ({
        url: `/admin/professional-submissions/${id}/reject`,
        method: "POST",
        body: dto || {}
      }),
      invalidatesTags: ["ProfessionalSubmissions"]
    }),
    // Articles
    createArticle: builder.mutation({
      query: body => ({
        url: "/articles",
        method: "POST",
        body
      }),
      invalidatesTags: ["Articles"]
    }),
    getArticles: builder.query({
      query: () => ({
        url: "/articles",
        method: "GET"
      }),
      providesTags: ["Articles"]
    }),
    getArticlesByOrganization: builder.query({
      query: organizationId => ({
        url: `/articles?organizationId=${organizationId}`,
        method: "GET"
      }),
      providesTags: ["Articles"],
      keepUnusedDataFor: 300
    }),
    getArticleById: builder.query({
      query: id => ({
        url: `/articles/${id}`,
        method: "GET"
      }),
      providesTags: (result, error, id) => [{
        type: "Articles",
        id
      }]
    }),
    updateArticle: builder.mutation({
      query: ({
        id,
        patch
      }) => ({
        url: `/articles/${id}`,
        method: "PATCH",
        body: patch
      }),
      invalidatesTags: (result, error, {
        id
      }) => [{
        type: "Articles",
        id
      }, "Articles"]
    }),
    deleteArticle: builder.mutation({
      query: id => ({
        url: `/articles/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Articles",
        id
      }, "Articles"]
    }),
    deleteArticleUpdate: builder.mutation({
      query: ({
        id,
        timestamp
      }) => ({
        url: `/articles/${encodeURIComponent(id)}/updates/${encodeURIComponent(timestamp)}`,
        method: "DELETE"
      }),
      invalidatesTags: (result, error, {
        id
      }) => [{
        type: "Articles",
        id
      }, "Articles"]
    }),
    deactivateArticle: builder.mutation({
      query: id => ({
        url: `/articles/${id}/deactivate`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Articles",
        id
      }, "Articles"]
    }),
    reactivateArticle: builder.mutation({
      query: id => ({
        url: `/articles/${id}/reactivate`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Articles",
        id
      }, "Articles"]
    }),
    approveArticle: builder.mutation({
      query: id => ({
        url: `/articles/${id}/approve`,
        method: "PATCH"
      }),
      invalidatesTags: (result, error, id) => [{
        type: "Articles",
        id
      }, "Articles"]
    }),
    getPendingArticles: builder.query({
      query: () => ({
        url: "/articles/pending",
        method: "GET"
      }),
      providesTags: ["Articles"]
    }),
    getDeactivatedArticles: builder.query({
      query: () => ({
        url: "/articles/deactivated",
        method: "GET"
      }),
      providesTags: ["Articles"]
    }),
    // Article Submissions
    submitArticle: builder.mutation({
      query: body => ({
        url: "/article-submissions",
        method: "POST",
        body
      }),
      invalidatesTags: ["ArticleSubmissions"]
    }),
    submitGuestArticle: builder.mutation({
      query: body => ({
        url: "/public/article-submissions/guest",
        method: "POST",
        body
      }),
      invalidatesTags: ["ArticleSubmissions"]
    }),
    deleteGuestArticleSubmission: builder.mutation({
      query: id => ({
        url: `/admin/article-submissions/guest/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["ArticleSubmissions"]
    }),
    getMyArticleSubmissions: builder.query({
      query: () => ({
        url: "/article-submissions/mine",
        method: "GET"
      }),
      providesTags: ["ArticleSubmissions"]
    }),
    getArticleSubmissionTasks: builder.query({
      query: id => ({
        url: `/article-submissions/${id}/tasks`,
        method: "GET"
      }),
      providesTags: ["Tasks"]
    }),
    replyToArticleTask: builder.mutation({
      query: ({
        submissionId,
        editId,
        ...body
      }) => ({
        url: `/article-submissions/${submissionId}/tasks/${editId}/messages`,
        method: "POST",
        body
      }),
      invalidatesTags: ["Tasks"]
    }),
    getArticleDeactivationHistory: builder.query({
      query: slug => ({
        url: `/article-submissions/deactivation-history/${encodeURIComponent(slug)}`,
        method: "GET"
      })
    }),
    // Admin Article Submissions
    getAdminArticleSubmissions: builder.query({
      query: params => ({
        url: "/admin/article-submissions",
        method: "GET",
        params: params || undefined
      }),
      providesTags: ["ArticleSubmissions"]
    }),
    approveArticleSubmission: builder.mutation({
      query: id => ({
        url: `/admin/article-submissions/${id}/approve`,
        method: "POST"
      }),
      invalidatesTags: ["ArticleSubmissions", "Articles"]
    }),
    rejectArticleSubmission: builder.mutation({
      query: ({
        id,
        dto
      }) => ({
        url: `/admin/article-submissions/${id}/reject`,
        method: "POST",
        body: dto || {}
      }),
      invalidatesTags: ["ArticleSubmissions", "Tasks"]
    }),
    getAdminArticleDeactivationHistory: builder.query({
      query: slug => ({
        url: `/admin/article-submissions/deactivation-history/${encodeURIComponent(slug)}`,
        method: "GET"
      })
    }),
    // ── Admin master list for website requested edits ──
    getAdminTasks: builder.query({
      query: params => {
        const q = new URLSearchParams();
        if (params && params.pageType) q.set("pageType", params.pageType);
        if (params && params.status) q.set("status", params.status);
        if (params && params.pagePath) q.set("pagePath", params.pagePath);
        if (params && params.scheduled !== undefined) q.set("scheduled", String(params.scheduled));
        if (params && params.backlog !== undefined) q.set("backlog", String(params.backlog));
        const qs = q.toString();
        return `/admin/tasks${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["Tasks"]
    }),
    updateTaskStatus: builder.mutation({
      query: ({
        id,
        status
      }) => ({
        url: `/admin/tasks/${id}/status`,
        method: "PATCH",
        body: {
          status
        }
      }),
      invalidatesTags: ["Tasks"],
      async onQueryStarted({
        id,
        status
      }, {
        dispatch,
        queryFulfilled,
        getState
      }) {
        // Find all cached getAdminTasks queries and optimistically update them
        const state = getState();
        const queries = state[api.reducerPath]?.queries ?? {};
        const patches = [];
        for (const key of Object.keys(queries)) {
          if (!key.startsWith("getAdminTasks(")) continue;
          const entry = queries[key];
          if (!entry || entry.status !== "fulfilled") continue;
          // Parse the original args from the cache key
          const argsStr = key.slice("getAdminTasks(".length, -1);
          let originalArgs;
          try {
            originalArgs = JSON.parse(argsStr);
          } catch {
            originalArgs = undefined;
          }
          patches.push(dispatch(api.util.updateQueryData("getAdminTasks", originalArgs, draft => {
            const idx = draft.findIndex(e => e.id === id);
            if (idx !== -1) {
              // If the list is filtered by a status that no longer matches, remove the item
              if (originalArgs?.status && originalArgs.status !== status) {
                draft.splice(idx, 1);
              } else {
                draft[idx].status = status;
              }
            }
          })));
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach(p => p.undo());
        }
      }
    }),
    deleteTask: builder.mutation({
      query: id => ({
        url: `/admin/tasks/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Tasks"],
      async onQueryStarted(id, {
        dispatch,
        queryFulfilled,
        getState
      }) {
        const state = getState();
        const queries = state[api.reducerPath]?.queries ?? {};
        const patches = [];
        for (const key of Object.keys(queries)) {
          if (!key.startsWith("getAdminTasks(")) continue;
          const entry = queries[key];
          if (!entry || entry.status !== "fulfilled") continue;
          const argsStr = key.slice("getAdminTasks(".length, -1);
          let originalArgs;
          try {
            originalArgs = JSON.parse(argsStr);
          } catch {
            originalArgs = undefined;
          }
          patches.push(dispatch(api.util.updateQueryData("getAdminTasks", originalArgs, draft => {
            const idx = draft.findIndex(e => e.id === id);
            if (idx !== -1) draft.splice(idx, 1);
          })));
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach(p => p.undo());
        }
      }
    }),
    createWebsiteTask: builder.mutation({
      query: body => ({
        url: "/admin/tasks",
        method: "POST",
        body
      }),
      invalidatesTags: ["Tasks"]
    }),
    updateWebsiteTask: builder.mutation({
      query: ({
        id,
        ...body
      }) => ({
        url: `/admin/tasks/${id}`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["Tasks"]
    }),
    addWebsiteTaskMessage: builder.mutation({
      query: ({
        id,
        ...msgBody
      }) => ({
        url: `/admin/tasks/${id}/messages`,
        method: "POST",
        body: msgBody
      }),
      invalidatesTags: ["Tasks"]
    }),
    addWebsiteTaskScreenshot: builder.mutation({
      query: ({
        id,
        ...ssBody
      }) => ({
        url: `/admin/tasks/${id}/screenshots`,
        method: "POST",
        body: ssBody
      }),
      invalidatesTags: ["Tasks"]
    }),
    removeWebsiteTaskScreenshot: builder.mutation({
      query: ({
        id,
        screenshotId
      }) => ({
        url: `/admin/tasks/${id}/screenshots/${screenshotId}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Tasks"]
    }),
    addTaskAttachment: builder.mutation({
      query: ({
        id,
        ...body
      }) => ({
        url: `/admin/tasks/${id}/attachments`,
        method: "POST",
        body
      }),
      invalidatesTags: ["Tasks"]
    }),
    removeTaskAttachment: builder.mutation({
      query: ({
        id,
        attachmentId
      }) => ({
        url: `/admin/tasks/${id}/attachments/${attachmentId}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Tasks"]
    }),
    bulkReorderTasks: builder.mutation({
      query: body => ({
        url: "/admin/tasks/reorder",
        method: "PATCH",
        body
      })
      // No invalidatesTags here — the Kanban board manually calls
      // refetch() after save to avoid race conditions with the
      // apiTasks→items sync effect.
    }),
    sendTaskNotification: builder.mutation({
      query: ({
        taskId,
        emails,
        messageId
      }) => ({
        url: `/admin/tasks/${taskId}/notify`,
        method: "POST",
        body: {
          emails,
          ...(messageId ? {
            messageId
          } : {})
        }
      })
    }),
    // Organization Inquiries
    submitOrganizationInquiry: builder.mutation({
      query: body => ({
        url: "/organization-inquiries",
        method: "POST",
        body
      }),
      invalidatesTags: ["OrganizationInquiries"]
    }),
    getOrganizationInquiries: builder.query({
      query: () => ({
        url: "/organization-inquiries",
        method: "GET"
      }),
      providesTags: ["OrganizationInquiries"]
    }),
    /** Org ids with no owner — powers the "Claim my Organization" buttons. */
    getUnclaimedOrganizations: builder.query({
      query: () => ({
        url: "/organizations/unclaimed",
        method: "GET"
      }),
      keepUnusedDataFor: 300,
      providesTags: ["OrganizationInquiries"]
    }),
    submitOrganizationClaim: builder.mutation({
      query: body => ({
        url: "/organization-inquiries/claim",
        method: "POST",
        body
      }),
      invalidatesTags: ["OrganizationInquiries"]
    }),
    approveOrganizationClaim: builder.mutation({
      query: id => ({
        url: `/organization-inquiries/${id}/approve-claim`,
        method: "POST"
      }),
      invalidatesTags: ["OrganizationInquiries"]
    }),
    rejectOrganizationClaim: builder.mutation({
      query: ({
        id,
        message
      }) => ({
        url: `/organization-inquiries/${id}/reject-claim`,
        method: "POST",
        body: {
          message
        }
      }),
      invalidatesTags: ["OrganizationInquiries"]
    }),
    deleteOrganizationInquiry: builder.mutation({
      query: id => ({
        url: `/organization-inquiries/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["OrganizationInquiries"]
    }),
    // Newsletter Signups
    submitNewsletterSignup: builder.mutation({
      query: body => ({
        url: "/newsletter-signups",
        method: "POST",
        body
      }),
      invalidatesTags: ["NewsletterSignups"]
    }),
    getNewsletterSignups: builder.query({
      query: () => ({
        url: "/newsletter-signups",
        method: "GET"
      }),
      providesTags: ["NewsletterSignups"]
    }),
    deleteNewsletterSignup: builder.mutation({
      query: id => ({
        url: `/newsletter-signups/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["NewsletterSignups"]
    }),
    // ── Promotions ──
    getPromotions: builder.query({
      // Cache-first: placements are version-gated in localStorage
      // (getPromotionsCache) so steady-state page loads render promoted
      // banners with zero backend requests. Rows mirror the server
      // verbatim; invalidation is version-stamp mismatch or the 24h
      // TTL, plus admin promotion mutations clearing the cache
      // directly.
      async queryFn(placement, _api, _extra, baseQuery) {
        const ls = await import("@/utils/localStore");
        const cached = ls.getPromotionsCache(placement);
        if (cached) return {
          data: cached
        };
        // Ticket BEFORE the fetch: stamps the response with the
        // version it could have been computed under, and lets a
        // concurrent clearPromotionsCache void this write.
        const ticket = ls.getPromotionsWriteTicket();
        const res = await baseQuery({
          url: `/promotions?placement=${encodeURIComponent(placement)}`,
          method: "GET"
        });
        if (res.error) return {
          error: res.error
        };
        const data = res.data;
        ls.setPromotionsCache(placement, data, ticket);
        return {
          data
        };
      },
      providesTags: ["Promotions"]
    }),
    getAllPromotions: builder.query({
      query: () => ({
        url: "/promotions/all",
        method: "GET"
      }),
      providesTags: ["Promotions"]
    }),
    getPromotionLogs: builder.query({
      query: () => ({
        url: "/promotions/logs",
        method: "GET"
      }),
      providesTags: ["PromotionLogs"]
    }),
    createPromotion: builder.mutation({
      query: body => ({
        url: "/promotions",
        method: "POST",
        body
      }),
      invalidatesTags: ["Promotions"],
      async onQueryStarted() {
        // Cleared at mutation START so the tag-invalidation refetch
        // can never race a stale placement-cache read.
        (await import("@/utils/localStore")).clearPromotionsCache();
      }
    }),
    updatePromotion: builder.mutation({
      query: ({
        id,
        ...body
      }) => ({
        url: `/promotions/${id}`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["Promotions"],
      async onQueryStarted() {
        (await import("@/utils/localStore")).clearPromotionsCache();
      }
    }),
    reorderPromotion: builder.mutation({
      query: ({
        id,
        direction
      }) => ({
        url: `/promotions/${id}/reorder`,
        method: "PATCH",
        body: {
          direction
        }
      }),
      invalidatesTags: ["Promotions"],
      async onQueryStarted() {
        (await import("@/utils/localStore")).clearPromotionsCache();
      }
    }),
    deletePromotion: builder.mutation({
      query: ({
        id,
        targetTitle,
        removedByEmail
      }) => ({
        url: `/promotions/${id}`,
        method: "DELETE",
        body: targetTitle || removedByEmail ? {
          targetTitle,
          removedByEmail
        } : undefined
      }),
      invalidatesTags: ["Promotions", "PromotionLogs"],
      async onQueryStarted() {
        (await import("@/utils/localStore")).clearPromotionsCache();
      }
    }),
    updateBannerTypeForPlacement: builder.mutation({
      query: ({
        placement,
        bannerType
      }) => ({
        url: `/promotions/placement/${encodeURIComponent(placement)}/banner-type`,
        method: "PATCH",
        body: {
          bannerType
        }
      }),
      invalidatesTags: ["Promotions"],
      async onQueryStarted() {
        (await import("@/utils/localStore")).clearPromotionsCache();
      }
    })
  })
});

/** Admin bid-queue row: full amounts + boost-adjusted rank rate. */

/** One account with admin access (DB role, env allowlist, or both). */

/** Invoice raised by approving a promotion bid (admin-only surface). */

/** Range argument for the analytics summary (from/to override days). */

/** A traffic-spike day ("Instance of note"). */

export const {
  useCreateUserMutation,
  useGetUsersQuery,
  useGetUserByIdQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetDeactivationModeQuery,
  useSetDeactivationModeMutation,
  useCreateOrganizationMutation,
  useGetOrganizationsQuery,
  useGetOrganizationByIdQuery,
  useUpdateOrganizationMutation,
  useDeleteOrganizationMutation,
  useGetMyOrganizationsQuery,
  useSearchOrganizationsQuery,
  useCreateEventMutation,
  useGetEventsQuery,
  useGetEventsByOrganizationQuery,
  useGetEventByIdQuery,
  useUpdateEventMutation,
  useDeleteEventMutation,
  useDeactivateEventMutation,
  useReactivateEventMutation,
  useApproveEventMutation,
  useGetPendingEventsQuery,
  useGetDeactivatedEventsQuery,
  useSubmitEventMutation,
  useSubmitGuestEventMutation,
  useDeleteGuestEventSubmissionMutation,
  useGetMyEventSubmissionsQuery,
  useGetEventSubmissionTasksQuery,
  useReplyToEventTaskMutation,
  useGetAdminEventSubmissionsQuery,
  useApproveEventSubmissionMutation,
  useRejectEventSubmissionMutation,
  useDiscardEventSubmissionMutation,
  useCreateRegulationMutation,
  useGetRegulationsQuery,
  useGetRegulationsByOrganizationQuery,
  useGetProfessionalsByOrganizationQuery,
  useGetRegulationByIdQuery,
  useUpdateRegulationMutation,
  useDeleteRegulationMutation,
  useDeleteRegulationUpdateMutation,
  useDeactivateRegulationMutation,
  useReactivateRegulationMutation,
  useApproveRegulationMutation,
  useGetPendingRegulationsQuery,
  useGetDeactivatedRegulationsQuery,
  useSubmitRegulationMutation,
  useSubmitGuestRegulationMutation,
  useDeleteGuestRegulationSubmissionMutation,
  useGetMyRegulationSubmissionsQuery,
  useGetRegulationSubmissionTasksQuery,
  useReplyToRegulationTaskMutation,
  useGetAdminRegulationSubmissionsQuery,
  useApproveRegulationSubmissionMutation,
  useRejectRegulationSubmissionMutation,
  useGetProfessionalsQuery,
  useGetProfessionalByIdQuery,
  useGetMyProfessionalQuery,
  useCreateProfessionalMutation,
  useUpdateProfessionalMutation,
  useDeleteProfessionalMutation,
  useDeactivateProfessionalMutation,
  useReactivateProfessionalMutation,
  useApproveProfessionalMutation,
  useGetPendingProfessionalsQuery,
  useGetDeactivatedProfessionalsQuery,
  useSubmitProfessionalMutation,
  useGetMyProfessionalSubmissionsQuery,
  useGetAdminProfessionalSubmissionsQuery,
  useApproveProfessionalSubmissionMutation,
  useRejectProfessionalSubmissionMutation,
  useCreateArticleMutation,
  useGetArticlesQuery,
  useGetArticlesByOrganizationQuery,
  useGetArticleByIdQuery,
  useUpdateArticleMutation,
  useDeleteArticleMutation,
  useDeleteArticleUpdateMutation,
  useDeactivateArticleMutation,
  useReactivateArticleMutation,
  useApproveArticleMutation,
  useGetPendingArticlesQuery,
  useGetDeactivatedArticlesQuery,
  useSubmitArticleMutation,
  useSubmitGuestArticleMutation,
  useDeleteGuestArticleSubmissionMutation,
  useGetMyArticleSubmissionsQuery,
  useGetArticleSubmissionTasksQuery,
  useReplyToArticleTaskMutation,
  useGetAdminArticleSubmissionsQuery,
  useApproveArticleSubmissionMutation,
  useRejectArticleSubmissionMutation,
  useGetArticleDeactivationHistoryQuery,
  useGetRegulationDeactivationHistoryQuery,
  useGetEventDeactivationHistoryQuery,
  useGetAdminRegulationDeactivationHistoryQuery,
  useGetAdminArticleDeactivationHistoryQuery,
  useGetAdminEventDeactivationHistoryQuery,
  useGetAdminTasksQuery,
  useUpdateTaskStatusMutation,
  useDeleteTaskMutation,
  useCreateWebsiteTaskMutation,
  useUpdateWebsiteTaskMutation,
  useAddWebsiteTaskMessageMutation,
  useAddWebsiteTaskScreenshotMutation,
  useRemoveWebsiteTaskScreenshotMutation,
  useAddTaskAttachmentMutation,
  useRemoveTaskAttachmentMutation,
  useBulkReorderTasksMutation,
  useSendTaskNotificationMutation,
  useSubmitOrganizationInquiryMutation,
  useGetOrganizationInquiriesQuery,
  useDeleteOrganizationInquiryMutation,
  useGetUnclaimedOrganizationsQuery,
  useSubmitOrganizationClaimMutation,
  useApproveOrganizationClaimMutation,
  useRejectOrganizationClaimMutation,
  useSubmitNewsletterSignupMutation,
  useGetNewsletterSignupsQuery,
  useDeleteNewsletterSignupMutation,
  useGetPromotionsQuery,
  useGetAllPromotionsQuery,
  useGetPromotionLogsQuery,
  useCreatePromotionMutation,
  useUpdatePromotionMutation,
  useReorderPromotionMutation,
  useDeletePromotionMutation,
  useUpdateBannerTypeForPlacementMutation,
  useGetPromotionModeQuery,
  useSetPromotionModeMutation,
  useGetSiteSettingsQuery,
  useUpdateSiteSettingsMutation,
  useGetAnalyticsSummaryQuery,
  useGetAnalyticsNotableDaysQuery,
  useGetAdminTeamQuery,
  useGetAdminPromotionBidsQuery,
  useDeclinePromotionBidMutation,
  useApprovePromotionBidMutation,
  useGetAdminPromotionInvoicesQuery,
  useMarkPromotionInvoicePaidMutation,
  useRunAnalyticsRollupMutation,
  useSubmitSuggestedEditMutation,
  useGetSuggestedEditsQuery,
  useDeleteSuggestedEditMutation
} = api;